import type {
  ObjConstraintType,
  StripPos,
} from '../shared/types';
// The command bar's vocabulary: the AutoCAD-style names and aliases a user can
// type, each mapped to the toolbar button that runs it. Running a command CLICKS
// that button, so a typed command does exactly what the toolbar does — there is
// no second code path to drift. DOM-free and unit-tested.

export interface CommandDef {
  /** Canonical name, echoed in the history (`Command: CIRCLE`). */
  name: string;
  /** Id of the toolbar button the command clicks. */
  button: string;
  /** Other spellings that run it (AutoCAD's short forms first). */
  aliases: string[];
}

/** One command per button, so a toolbar-started tool echoes a single name. */
export const COMMANDS: CommandDef[] = [
  // Draw
  { name: 'LINE', button: 'btn-line', aliases: ['L', 'PL', 'PLINE'] },
  { name: 'WALL', button: 'btn-wall', aliases: ['W'] },
  { name: 'RECTANG', button: 'btn-rectangle', aliases: ['REC', 'RECT', 'RECTANGLE'] },
  { name: 'POLYGON', button: 'btn-polygon', aliases: ['POL'] },
  { name: 'CIRCLE', button: 'btn-circle', aliases: ['C'] },
  { name: 'ARC', button: 'btn-arc', aliases: ['A'] },
  // Annotate
  { name: 'DIM', button: 'btn-dimension', aliases: ['D', 'DLI', 'DIMLINEAR', 'DAL', 'DIMALIGNED'] },
  { name: 'MEASURE', button: 'btn-measure', aliases: ['DI', 'DIST', 'MEA', 'MEASUREGEOM'] },
  { name: 'TEXT', button: 'btn-text', aliases: ['T', 'MT', 'MTEXT', 'DT', 'DTEXT'] },
  { name: 'LEADER', button: 'btn-leader', aliases: ['LE', 'MLD', 'MLEADER'] },
  { name: 'REVCLOUD', button: 'btn-revcloud', aliases: ['REVISIONCLOUD'] },
  // Modify
  { name: 'OFFSET', button: 'btn-offset', aliases: ['O'] },
  { name: 'TRIM', button: 'btn-trim', aliases: ['TR', 'EX', 'EXTEND'] },
  { name: 'FILLET', button: 'btn-fillet', aliases: ['F', 'CHA', 'CHAMFER'] },
  { name: 'MIRROR', button: 'btn-mirror', aliases: ['MI'] },
  { name: 'CONSTRAIN', button: 'btn-constrain', aliases: ['GC', 'GEOMCONSTRAINT', 'RELATION'] },
  { name: 'EDITPOLYGON', button: 'btn-edit-polygon', aliases: ['PE', 'PEDIT'] },
  { name: 'EXPLODE', button: 'btn-explode', aliases: ['X'] },
  { name: 'JOIN', button: 'btn-join', aliases: ['J'] },
  // Edit: Move / Copy / Rotate pick a base point, AutoCAD-style; Scale (and the
  // numeric Move / Rotate) live in the Transform panel.
  { name: 'MOVE', button: 'btn-move', aliases: ['M'] },
  { name: 'COPY', button: 'btn-copy', aliases: ['CO', 'CP'] },
  { name: 'ROTATE', button: 'btn-rotate', aliases: ['RO'] },
  { name: 'TRANSFORM', button: 'btn-transform', aliases: ['SC', 'SCALE'] },
  { name: 'ARRAY', button: 'btn-array-rect', aliases: ['AR', 'ARRAYRECT'] },
  { name: 'ARRAYPOLAR', button: 'btn-array-polar', aliases: [] },
  { name: 'ERASE', button: 'btn-delete', aliases: ['E', 'DEL', 'DELETE'] },
  // View
  { name: 'ZOOM', button: 'btn-zoom-window', aliases: ['Z'] },
  { name: 'UNDO', button: 'btn-undo', aliases: ['U'] },
  { name: 'REDO', button: 'btn-redo', aliases: [] },
  // Sauna / room features
  { name: 'SECTION', button: 'btn-section', aliases: ['SEC'] },
  { name: 'SECTIONPLANE', button: 'btn-section-3d', aliases: ['SPLANE'] },
  { name: 'CEILINGPLAN', button: 'btn-rcp', aliases: ['RCP'] },
  { name: 'ABOVECEILING', button: 'btn-ceiling-top', aliases: ['ACP'] },
  { name: 'EDITROOM', button: 'btn-edit-room', aliases: ['ER'] },
  { name: 'GLASS', button: 'btn-glass', aliases: [] },
  { name: 'OPENING', button: 'btn-opening', aliases: [] },
  { name: 'DOOR', button: 'btn-door', aliases: [] },
  { name: 'INNERWALL', button: 'btn-inner-wall', aliases: ['IW'] },
  { name: 'BENCH', button: 'btn-bench', aliases: [] },
  { name: 'BENCHSUPPORT', button: 'btn-bench-support', aliases: ['BSUP'] },
  { name: 'BENCHEND', button: 'btn-bench-end', aliases: ['BEND'] },
  { name: 'BENCHROUND', button: 'btn-bench-round', aliases: ['BROUND'] },
  { name: 'HEATERWALL', button: 'btn-heater-wall', aliases: ['HW'] },
  { name: 'BACKREST', button: 'btn-backrest', aliases: ['BREST'] },
  { name: 'SCULPTBENCH', button: 'btn-sculpt-bench', aliases: ['SCULPT'] },
  { name: 'CONNECTION', button: 'btn-connection', aliases: ['CONNECT'] },
  { name: 'SCALETOMEASURE', button: 'btn-pdf-scale', aliases: ['PDFSCALE', 'STM'] },
  // Flooring
  { name: 'FLOORTILED', button: 'btn-floor-tiled', aliases: [] },
  { name: 'FLOORTILEDSLOPE', button: 'btn-floor-tiled-slope', aliases: [] },
  { name: 'FLOORSCREED', button: 'btn-floor-screed', aliases: [] },
  { name: 'FLOORSCREEDSLOPE', button: 'btn-floor-screed-slope', aliases: [] },
  { name: 'FLOORGRILLE', button: 'btn-floor-grille', aliases: ['GRILLE'] },
  // Lighting
  { name: 'CHANNELLIGHT', button: 'btn-light-channel', aliases: ['CHANNEL'] },
  { name: 'COVELIGHT', button: 'btn-light-cove', aliases: ['COVE'] },
  { name: 'SPOTLIGHT', button: 'btn-light-spot', aliases: ['SPOT'] },
  { name: 'STARRYSKY', button: 'btn-light-sky', aliases: ['SKY'] },
  { name: 'STRIPLIGHT', button: 'btn-light-strip', aliases: ['STRIP', 'LS', 'STRIPDOWN'] },
  { name: 'STRIPUP', button: 'btn-light-strip-up', aliases: ['LSU'] },
];

/** The command a typed string names, or null. Case-insensitive and trimmed; a
 *  leading `_` (AutoCAD's language-neutral prefix) is ignored. */
export function resolveCommand(raw: string): CommandDef | null {
  const key = raw.trim().replace(/^_+/, '').toUpperCase();
  if (!key) return null;
  return COMMANDS.find((c) => c.name === key || c.aliases.includes(key)) ?? null;
}

/** The command a toolbar button runs — how a tool started from the toolbar is
 *  echoed in the history under the same name a typed one would be. */
export function commandForButton(button: string): CommandDef | null {
  return COMMANDS.find((c) => c.button === button) ?? null;
}

/** What a command can be FOUND by besides its name and aliases — PowerPoint's
 *  "Tell me what you want to do". `labels` are its names as a person says them
 *  (the toolbar label, English and translated); `hints` are its descriptions (the
 *  tooltip), matched only word-by-word and ranked below every label. */
export interface CommandSearchEntry {
  cmd: CommandDef;
  labels: string[];
  hints?: string[];
}

/** Lower-case, accents and icons dropped, runs of anything else a single space —
 *  so `Épaisseur`, `epaisseur` and `📏 Quota` compare by their letters alone. */
export function searchKey(s: string): string {
  return s.normalize('NFD').replace(/\p{M}+/gu, '').toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

/** How well `q` (a search key) matches the text `s` (a search key): 0 for not at
 *  all. Whole text first, then its start, then every typed word starting one of
 *  its words (in any order), then anywhere inside it, and last — for three letters
 *  or more — the letters in order with gaps (`rct` → `rect`), tighter first. */
function textScore(q: string, s: string): number {
  if (!s) return 0;
  if (s === q) return 900;
  if (s.startsWith(q)) return 800;
  const words = s.split(' ');
  if (q.split(' ').every((w) => words.some((x) => x.startsWith(w)))) return 700;
  const flat = s.replace(/ /g, ''), fq = q.replace(/ /g, '');
  if (flat.includes(fq)) return 500;
  if (fq.length < 3) return 0;
  let i = 0, first = -1, last = -1;
  for (let k = 0; k < flat.length && i < fq.length; k++) {
    if (flat[k] === fq[i]) { if (first < 0) first = k; last = k; i++; }
  }
  if (i < fq.length) return 0;
  return Math.max(1, 300 - (last - first + 1 - fq.length) * 10);
}

/** The commands a typed search finds, best first (at most `limit`). A command's
 *  name or alias typed exactly ranks top; one being typed (`CIR`) next; then its
 *  labels in any language; its tooltip words last. Equal scores keep the
 *  entries' order, so the list never reshuffles between keystrokes. */
export function searchCommands(query: string, entries: readonly CommandSearchEntry[], limit = 8): CommandDef[] {
  const q = searchKey(query);
  if (!q) return [];
  const scored: { cmd: CommandDef; score: number; i: number }[] = [];
  entries.forEach(({ cmd, labels, hints }, i) => {
    const names = [cmd.name, ...cmd.aliases].map(searchKey);
    let score = 0;
    if (names.includes(q)) score = 1000;
    else if (names.some((n) => n.startsWith(q))) score = 850;
    for (const l of labels) score = Math.max(score, textScore(q, searchKey(l)));
    if (!score && q.length >= 3) {
      const words = q.split(' ');
      for (const h of hints ?? []) {
        const hw = searchKey(h).split(' ');
        if (words.every((w) => hw.some((x) => x.startsWith(w)))) { score = 200; break; }
      }
    }
    if (score) scored.push({ cmd, score, i });
  });
  scored.sort((a, b) => b.score - a.score || a.i - b.i);
  return scored.slice(0, limit).map((s) => s.cmd);
}

/** Whether a typed string is a distance / coordinate for the drawing tool in
 *  progress rather than a command: it starts the way typed entry does (a digit,
 *  `.`, `@` for relative, or `-`). */
export function looksLikeEntry(raw: string): boolean {
  return /^[0-9.@-]/.test(raw.trim());
}

/** An option of the command in progress — AutoCAD's `[Close/Undo]`: typing its
 *  letter (or the whole word) acts on the draft instead of starting a command. */
export interface ToolOption {
  id: 'close' | 'undo' | 'erase' | 'keep' | 'through' | 'extents' | 'object' | 'electrical' | 'plumbing' | 'mechanical' | 'drainage' | 'straight' | 'orthogonal' | 'fibre' | 'spot' | 'wall' | 'perimeter' | 'centre' | 'runs' | 'offset' | 'gap' | 'spacing' | 'margin' | 'across' | 'along' | 'zone' | 'ceiling' | 'fifty' | 'hundred' | 'both' | 'surface' | 'xy' | 'yz' | 'zx' | 'flip' | 'horizontal' | 'angle' | ObjConstraintType | StripPos;
  /** English word, shown translated in the prompt; its first letter is the key. */
  label: string;
}

export const CLOSE_OPTION: ToolOption = { id: 'close', label: 'Close' };
export const UNDO_OPTION: ToolOption = { id: 'undo', label: 'Undo' };
/** Mirror's choice: erase the source objects, or keep them (the default). */
export const ERASE_OPTION: ToolOption = { id: 'erase', label: 'Erase source' };
export const KEEP_OPTION: ToolOption = { id: 'keep', label: 'Keep source' };
/** Rotate's choice: turn to an angle (the default), or make the line from the
 *  pivot to a clicked point horizontal — level, in a section view. */
export const HORIZONTAL_OPTION: ToolOption = { id: 'horizontal', label: 'Horizontal' };
export const ANGLE_OPTION: ToolOption = { id: 'angle', label: 'Angle' };
/** Offset's choice once a distance is typed: back to passing through the click. */
export const THROUGH_OPTION: ToolOption = { id: 'through', label: 'Through' };
/** ZOOM's choices besides a window: everything, or the selection. */
export const EXTENTS_OPTION: ToolOption = { id: 'extents', label: 'Extents' };
export const OBJECT_OPTION: ToolOption = { id: 'object', label: 'Object' };
/** CONNECT's disciplines: typing one sets what the connection being drawn (and
 *  every next one) is born as. */
/** CONNECT's routing: the one not in use is offered (as Mirror offers its other
 *  choice), and it sticks for the next connection too. */
export const STRAIGHT_OPTION: ToolOption = { id: 'straight', label: 'Straight' };
export const RIGHT_ANGLE_OPTION: ToolOption = { id: 'orthogonal', label: 'Right-angle' };
export const CONNECTION_TYPE_OPTIONS: readonly ToolOption[] = [
  { id: 'electrical', label: 'Electrical' },
  { id: 'plumbing', label: 'Plumbing' },
  { id: 'mechanical', label: 'Mechanical' },
  { id: 'drainage', label: 'Drainage' },
];

/** The Spot tool's choice: the next lights are single fibre ends, or spots again. */
export const FIBRE_OPTION: ToolOption = { id: 'fibre', label: 'Fibre' };
export const SPOT_OPTION: ToolOption = { id: 'spot', label: 'Spot' };
/** The Channel tool's layouts: along a wall, down the room's middle, or in runs
 *  across it — each typed again goes back to drawing one freely. */
export const CHANNEL_OPTIONS: readonly ToolOption[] = [
  { id: 'wall', label: 'Wall' },
  { id: 'perimeter', label: 'Perimeter' },
  { id: 'centre', label: 'Centre' },
  { id: 'runs', label: 'Runs' },
];
/** The Channel tool's figures, each taking the number typed next: how far in
 *  from the walls, the gap at the ends, the runs' spacing and margin; and the
 *  way the runs go, the one not in use offered. */
export const CHANNEL_FIGURE_OPTIONS = {
  offset: { id: 'offset', label: 'Offset' },
  gap: { id: 'gap', label: 'Gap' },
  spacing: { id: 'spacing', label: 'Spacing' },
  margin: { id: 'margin', label: 'Margin' },
  across: { id: 'across', label: 'Across' },
  along: { id: 'along', label: 'Along' },
} as const satisfies Record<string, ToolOption>;
/** The Starry sky tool's choices, the one not in use offered: a zone drawn by
 *  hand or the whole ceiling, and the kit — 50 or 100 fibres. */
export const ZONE_OPTION: ToolOption = { id: 'zone', label: 'Zone' };
/** The 3D section's choices: the plane a new section is laid on — the surface
 *  under the pointer, or AutoCAD's XY / YZ / ZX —, and, once one is placed, Flip. */
export const SECTION3D_OPTIONS: readonly ToolOption[] = [
  { id: 'surface', label: 'Surface' },
  { id: 'xy', label: 'XY' },
  { id: 'yz', label: 'YZ' },
  { id: 'zx', label: 'ZX' },
];
export const FLIP_OPTION: ToolOption = { id: 'flip', label: 'Flip' };
export const CEILING_OPTION: ToolOption = { id: 'ceiling', label: 'Whole ceiling' };
export const SKY_KIT_OPTIONS: Record<50 | 100, ToolOption> = { 50: { id: 'fifty', label: '50' }, 100: { id: 'hundred', label: '100' } };
/** The Strip tool's positions: typing one puts the next strips there. */
/** The strip positions each Strip tool takes typed: shining down, shining up. */
export const STRIP_DOWN_OPTIONS: readonly ToolOption[] = (['A1', 'A2', 'D'] as const).map((id) => ({ id, label: id }));
export const STRIP_UP_OPTIONS: readonly ToolOption[] = (['B', 'C'] as const).map((id) => ({ id, label: id }));
/** Either Strip tool's choice for a backrest: its up and its down strip at once. */
export const STRIP_BOTH_OPTION: ToolOption = { id: 'both', label: 'Up + down' };

/** The option a typed string picks: its first letter, or the whole word in
 *  English or in the UI language (`t`). Case-insensitive; null when none. */
export function resolveOption(raw: string, options: readonly ToolOption[], t: (en: string) => string = (s) => s): ToolOption | null {
  const key = raw.trim().replace(/^_+/, '').toUpperCase();
  if (!key) return null;
  return options.find((o) => {
    const words = [o.label, t(o.label)].map((w) => w.toUpperCase());
    return words.includes(key) || (key.length === 1 && words.some((w) => w[0] === key));
  }) ?? null;
}

/** The prompt suffix listing the options, e.g. `[Close/Undo]`; '' for none. */
export function optionsPrompt(options: readonly ToolOption[], t: (en: string) => string = (s) => s): string {
  return options.length ? `[${options.map((o) => t(o.label)).join('/')}]` : '';
}
